import { useEffect, useRef, useState } from 'react'
import { ApiError, toApiError } from '@/lib/api'
import { useAnswerApi } from '@/hooks/useAnswerApi'

/** The signed-in caller's role, resolved server-side from the validated JWT.
 * Drives whether the admin entry point is shown — the admin endpoints are
 * still enforced server-side, so this is UX only. */
export type CurrentUserState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; oid: string; role: string; isAdmin: boolean }
  | { kind: 'error'; message: string }

export type UseCurrentUserOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  fetchImpl?: typeof fetch
  /** Gate the load on authentication — fetching while unauthenticated would
   * record a token error as a role-lookup failure. */
  enabled?: boolean
}

export function useCurrentUser(options: UseCurrentUserOptions): CurrentUserState {
  const enabled = options.enabled ?? true
  const [state, setState] = useState<CurrentUserState>({ kind: 'idle' })
  const api = useAnswerApi(options)
  const apiRef = useRef(api)
  useEffect(() => {
    apiRef.current = api
  })

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setState({ kind: 'loading' })
    apiRef.current
      .currentUser()
      .then((me) => {
        if (cancelled) return
        setState({ kind: 'ready', oid: me.oid, role: me.role, isAdmin: me.isAdmin })
      })
      .catch(async (err) => {
        if (cancelled) return
        const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
        setState({ kind: 'error', message: apiErr.message })
      })
    return () => {
      cancelled = true
    }
  }, [enabled])

  return state
}
